import { useQuery } from '@tanstack/react-query';
import { useMemo, useEffect } from 'react';
import usePagination from 'common/fetchHooks/usePagination';
import { getUsers } from 'common/services/users.service';
import debounce from 'debounce-promise';

const getUsersDebounced = debounce(getUsers, 500);

const useUsersQuery = ({
  limit = 10,
  orderBy = 'name',
  orderDir = 'ASC',
  search = '',
  searchFields = ['name', 'email']
} = {}) => {
  const pagination = usePagination(limit);

  const { data, isLoading, error } = useQuery({
    retry: false,
    refetchOnWindowFocus: false,
    queryKey: [
      'users',
      pagination.qs.limit,
      pagination.qs.offset,
      orderBy,
      orderDir,
      search,
      searchFields
    ],
    queryFn: () =>
      getUsersDebounced({
        limit: pagination.qs.limit,
        offset: pagination.qs.offset,
        orderBy,
        orderDir,
        search,
        searchFields
      })
  });

  const users = useMemo(
    () => data?.responseObject?.users || [],
    [data?.responseObject?.users]
  );

  useEffect(() => {
    pagination.setTotal(data?.responseObject?.total || 0);
  }, [data?.responseObject?.total, pagination.setTotal]);

  return {
    data: users,
    total: pagination.total,
    isLoading,
    error,
    pagination
  };
};

export default useUsersQuery;
